const WHATSAPP_BASE_URL = 'https://wa.me';

/**
 * Converts a stored customer phone number to the international digits-only
 * format required by WhatsApp's wa.me links.
 *
 * Customer phone numbers are normally stored with a country code by
 * PhoneInput, but older records may still contain a Turkish local number.
 * Local Turkish numbers are therefore interpreted as +90 when no country
 * code is present.
 */
export const normalizeWhatsAppPhone = (phoneNumber?: string | null): string | null => {
  const value = phoneNumber?.trim();
  if (!value) return null;

  const digits = value.replace(/\D/g, '');
  if (!digits) return null;

  if (value.startsWith('00')) {
    const internationalDigits = digits.replace(/^00+/, '');
    return internationalDigits || null;
  }
  if (value.startsWith('+')) return digits;
  if (digits.startsWith('0')) {
    const localDigits = digits.replace(/^0+/, '');
    return localDigits ? `90${localDigits}` : null;
  }
  if (digits.length === 10 && digits.startsWith('5')) return `90${digits}`;
  return digits;
};

export interface WhatsAppUrlOptions {
  phoneNumber?: string | null;
  message?: string | null;
}

/** Builds every WhatsApp link used by the application in one place. */
export const buildWhatsAppUrl = ({ phoneNumber, message }: WhatsAppUrlOptions): string => {
  const normalizedPhone = normalizeWhatsAppPhone(phoneNumber);
  const recipient = normalizedPhone ? `${WHATSAPP_BASE_URL}/${normalizedPhone}` : `${WHATSAPP_BASE_URL}/`;
  return message ? `${recipient}?text=${encodeURIComponent(message)}` : recipient;
};

/** Opens a WhatsApp link and reports whether the browser allowed the popup. */
export const openWhatsApp = (options: WhatsAppUrlOptions): boolean => {
  const openedWindow = window.open(buildWhatsAppUrl(options), '_blank', 'noopener,noreferrer');
  return Boolean(openedWindow);
};
